import { create } from "zustand";
import {
  handleLoadMessages,
  handleSendMessage,
  handleSendReadedMessages,
  handleMarkMessagesAsRead,
  handleDeleteMessage,
  handleResendMessage,
  handleLoadFiles,
  handleUpdateMessage,
  handleForwardMessage,
} from "./handlers";
import { getMessageById, Message } from "@/entities/message";
import { User } from "@/entities/user";
import { Chat, InputTitle } from "@/entities/chat";
import { FileItem, FileType } from "@/shared/lib";
import { enqueueSnackbar } from "notistack";

export type MessageFile = {
  url: string;
  msgId: string;
  createdAt: number;
}

export type ActiveMsg = {
  msg: Message;
  type?: FileType;
  file?: FileItem;
}

export interface MessagesState {
  messages: Record<string, Message[]>;
  loadingMessage: Record<string, boolean>;
  loadingChatMessages: Record<string, boolean>;
  loadingMoreMessages: Record<string, boolean>;
  inputTitle: Record<string, InputTitle | null>;
  replyingToMessage: Record<string, Message | null>;
  editingMessage: Record<string, Message | null>;
  page: Record<string, number>;
  pages: Record<string, number[]>;
  totalCount: Record<string, number>;
  lastReadMessageIds: Record<string, string | null>;

  files: Record<string, MessageFile[]>;
  loadingFiles: Record<string, boolean>;

  activeMsg: Record<string, ActiveMsg | null>;

  // поле для хранения текущего состояния загрузки сообщения по ID
  loadingMessageById: Record<string, boolean>;
  loadMessageById: (chatId: string, messageId: string) => Promise<void>;

  getMessages: (chatId: string) => Message[];
  getFiles: (chatId: string) => MessageFile[];
  clearMessage: () => void;

  loadMessages: (chatId: string, page?: number, replace?: boolean) => Promise<void>;
  loadFiles: (chatId: string) => Promise<void>;
  sendMessage: (
    owner: User,
    chatId: string,
    receiverId: string,
    content: string,
    reply?: Message | null,
    edit?: Message | null,
    files?: File[]
  ) => void;
  resendMessage: (
    owner: User,
    message: Message,
    chatId: string,
    receiverId: string
  ) => void;
  updateMessage: (
    chatId: string,
    messageId: string,
    newText: string,
    newFiles: File[],
    oldFiles: string[]
  ) => Promise<void>;
  sendReadedMessages: (
    chatId: string,
    lastReadMessageId: string,
    currentUserId: string
  ) => void;
  markMessagesAsRead: (
    chatId: string,
    upToMessageId: string,
    currentUserId: string
  ) => { lastReadMessageId: string | null; unreadCount: number } | null;

  setInputTitle: (chatId: string, title: string) => void;
  setReplyingToMessage: (chatId: string, message: Message | null) => void;
  setForwardMessage: (
    fromChatId: string,
    messagesId: string[],
    toChatIds: string[],
  ) => void;
  setEditingMessage: (chatId: string, message: Message | null) => void;
  clearReplyingToMessage: (chatId: string) => void;
  clearEditingMessage: (chatId: string) => void;
  deleteMessage: (chatId: string, messageIds: string[]) => Promise<void>;
  setActiveMsg: (chatId: string, activeMsg: ActiveMsg | null) => void;
}

export const useMessagesStore = create<MessagesState>((set, get) => {
  const context = {
    getState: get,
    setState: set,
  };

  return {
    messages: {},
    loadingMessage: {},
    loadingChatMessages: {},
    loadingMoreMessages: {},
    lastReadMessageIds: {},
    inputTitle: {},
    replyingToMessage: {},
    editingMessage: {},
    page: {},
    pages: {},
    totalCount: {},

    files: {},
    loadingFiles: {},

    loadingMessageById: {},

    activeMsg: {},
    getMessages: (chatId) => get().messages[chatId] || [],
    getFiles: (chatId) => get().files[chatId] || [],

    clearMessage: () => {
      set({ messages: {} });
    },

    loadMessageById: async (chatId, messageId) => {
      if (!chatId || !messageId) return;

      set((state) => ({
        loadingMessageById: { ...state.loadingMessageById, [chatId]: true },
      }));

      try {
        const res = await getMessageById(chatId, messageId);

        if (res.success) {
          const { found, after, before, page } = res.result;

          // Получаем текущее состояние
          const currentMessages = get().messages[chatId] || [];
          const currentPage = get().page[chatId] || 1;

          let newMessages: Message[] = [];

          // Логика обновления стейта в зависимости от страницы
          if (page === undefined || page <= 1) {
            // Сообщение на первой странице или без указания страницы — заменяем все
            newMessages = [...before, found, ...after];
          } else if (page === currentPage + 1) {
            // Сообщение на следующей странице — добавляем после текущих
            newMessages = [...currentMessages, ...after];
            // Убедимся, что found есть в списке (если его нет, добавим)
            if (!newMessages.some((msg) => msg._id === found._id)) {
              newMessages = [...newMessages, found]; // Это крайний случай, но лучше перестраховаться
            }
          } else if (page > currentPage + 1) {
            // Сообщение на странице, которая сильно дальше — заменяем все
            newMessages = [...before, found, ...after];
          } else {
            // Сообщение на той же или предыдущей странице — заменяем все
            newMessages = [...before, found, ...after];
          }

          set((state) => ({
            messages: {
              ...state.messages,
              [chatId]: newMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
            },
            pages: {
              [chatId]: [page],
            },
            page: {
              ...state.page,
              [chatId]: page || 1,
            },
            loadingMessageById: { ...state.loadingMessageById, [chatId]: false },
          }));
        } else {

          enqueueSnackbar(`Не удалось загрузить сообщение`, { variant: "error" });
        }

      } catch (error) {
        console.error("Error loading message by ID:", error);
        set((state) => ({
          loadingMessageById: { ...state.loadingMessageById, [chatId]: false },
        }));
      }
    },

    loadMessages: (chatId, page, replace) => handleLoadMessages(context, chatId, page, replace),
    loadFiles: (chatId) => handleLoadFiles(context, chatId),

    sendMessage: (owner, chatId, receiverId, content, reply, edit, files) => {
      handleSendMessage(
        context,
        content,
        owner,
        chatId,
        receiverId,
        reply,
        edit,
        files
      );
    },
    resendMessage: (owner, message, chatId, receiverId) => {
      handleResendMessage(context, message, owner, chatId, receiverId);
    },
    updateMessage: (chatId, messageId, newText, newFiles, oldFiles) =>
      handleUpdateMessage(context, chatId, messageId, newText, newFiles, oldFiles),
    sendReadedMessages: (chatId, lastReadMessageId, currentUserId) =>
      handleSendReadedMessages(
        context,
        chatId,
        lastReadMessageId,
        currentUserId
      ),
    markMessagesAsRead: (chatId, upToMessageId, currentUserId) =>
      handleMarkMessagesAsRead(context, chatId, upToMessageId, currentUserId),
    deleteMessage: (chatId, messageIds) =>
      handleDeleteMessage(context, chatId, messageIds),

    setInputTitle: (chatId, title) => {
      set((state) => ({
        inputTitle: {
          ...state.inputTitle,
          [chatId]: { text: title, createdAt: Date.now() },
        },
      }));
    },

    setReplyingToMessage: (chatId, message) => {
      set((state) => ({
        replyingToMessage: { ...state.replyingToMessage, [chatId]: message },
      }));
    },
    clearReplyingToMessage: (chatId) => {
      set((state) => ({
        replyingToMessage: { ...state.replyingToMessage, [chatId]: null },
        inputTitle: { ...state.inputTitle, [chatId]: null },
      }));
    },

    setEditingMessage: (chatId, message) => {
      set((state) => ({
        editingMessage: { ...state.editingMessage, [chatId]: message },
        inputTitle: {
          ...state.inputTitle,
          [chatId]: { text: message?.text || "", createdAt: Date.now() },
        },
      }));
    },

    setForwardMessage: (fromChatId: string, messagesId: string[], toChatIds: string[]) => {
      handleForwardMessage(context, fromChatId, messagesId, toChatIds);
    },

    clearEditingMessage: (chatId) => {
      set((state) => ({
        editingMessage: { ...state.editingMessage, [chatId]: null },
        inputTitle: { ...state.inputTitle, [chatId]: null },
      }));
    },

    setActiveMsg: (chatId, message) => {
      set((state) => ({
        activeMsg: { ...state.activeMsg, [chatId]: message },
      }));
    },
  };
});
